<script setup>
import { ref, computed } from 'vue';
import { CONDITION_CATEGORIES, CONDITION_TYPES } from '../constants';
import { uid } from '../graph';

const emit = defineEmits(['select', 'close']);

const activeCategory = ref(CONDITION_CATEGORIES[0].id);

const currentCategory = computed(() =>
  CONDITION_CATEGORIES.find(c => c.id === activeCategory.value)
);

const selectCondition = condDef => {
  const ct = CONDITION_TYPES.find(t => t.value === condDef.value);
  const initialOptions = {};
  if (!ct?.noParameter) initialOptions.parameter = '';
  if (ct?.valueKey) initialOptions[ct.valueKey] = '';
  if (condDef.value === 'conversation-status-condition')
    initialOptions.status = 'open';

  emit('select', {
    id: uid(),
    name: condDef.value,
    group: activeCategory.value,
    options: initialOptions,
  });
};

const ICONS = {
  user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z',
  'message-circle':
    'M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z',
  'text-cursor-input':
    'M5 4h1a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H5M19 4h-1a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h1M12 2v20',
  briefcase:
    'M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2zM16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16',
  clock:
    'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zM12 6v6l4 2',
};

const condIcon = value => {
  if (value.startsWith('field-is-equal') || value.startsWith('field-not-equal'))
    return 'M5 9h14M5 15h14';
  if (value.includes('contains'))
    return 'M21 12H3M21 6H3M21 18H3';
  if (value.includes('present') || value.includes('has-name'))
    return 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zM12 8v4M12 16h0';
  if (value.includes('empty'))
    return 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z';
  if (value.includes('greater'))
    return 'M9 18l6-6-6-6';
  if (value.includes('less'))
    return 'M15 18l-6-6 6-6';
  if (value.includes('exists') || value.includes('has-email') || value.includes('has-phone'))
    return 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z';
  if (value.includes('label'))
    return 'M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82zM7 7h0';
  if (value.includes('status'))
    return 'M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4 12 14.01l-3-3';
  if (value.includes('agent'))
    return 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M17 11l2 2 4-4M9 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z';
  if (value.includes('team'))
    return 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75';
  return 'M22 3H2l8 9.46V19l4 2v-8.54L22 3';
};
</script>

<template>
  <Teleport to="body">
    <div class="cp-overlay" @click.self="emit('close')">
      <div class="cp-modal">
        <!-- Sidebar -->
        <nav class="cp-sidebar">
          <button
            v-for="cat in CONDITION_CATEGORIES"
            :key="cat.id"
            class="cp-nav-item"
            :class="{ 'cp-nav-active': activeCategory === cat.id }"
            @click="activeCategory = cat.id"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path :d="ICONS[cat.icon]" />
            </svg>
            <span>{{ cat.label }}</span>
          </button>
        </nav>

        <!-- Content -->
        <div class="cp-content">
          <div class="cp-content-header">
            <div>
              <h3 class="cp-content-title">{{ currentCategory.label }}</h3>
              <p class="cp-content-desc">{{ currentCategory.description }}</p>
            </div>
            <button class="cp-close-btn" @click="emit('close')">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div class="cp-list">
            <button
              v-for="cond in currentCategory.conditions"
              :key="cond.value"
              class="cp-condition-item"
              @click="selectCondition(cond)"
            >
              <div class="cp-cond-icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path :d="condIcon(cond.value)" />
                </svg>
              </div>
              <div class="cp-cond-text">
                <span class="cp-cond-title">{{ cond.title }}</span>
                <span class="cp-cond-desc">{{ cond.description }}</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.cp-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  animation: cp-fade-in 0.15s ease;
}

@keyframes cp-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

.cp-modal {
  display: flex;
  width: 640px;
  max-width: 95vw;
  max-height: 520px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  animation: cp-scale-in 0.2s ease;
}

@keyframes cp-scale-in {
  from { transform: scale(0.97); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.cp-sidebar {
  width: 180px;
  min-width: 180px;
  background: rgb(var(--slate-2));
  border-right: 1px solid rgb(var(--slate-4));
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cp-nav-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  border-radius: 8px;
  font-size: 13.5px;
  font-weight: 500;
  color: rgb(var(--slate-10));
  cursor: pointer;
  transition: all 0.12s ease;
  text-align: left;
  width: 100%;
}
.cp-nav-item:hover {
  color: rgb(var(--slate-12));
  background: rgb(var(--slate-3));
}
.cp-nav-active {
  color: rgb(var(--slate-12));
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  font-weight: 600;
}

.cp-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.cp-content-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1.25rem 1.5rem 0.75rem;
  flex-shrink: 0;
}

.cp-content-title {
  font-size: 18px;
  font-weight: 700;
  color: rgb(var(--slate-12));
  margin: 0;
}

.cp-content-desc {
  font-size: 13px;
  color: rgb(var(--slate-9));
  margin: 0.25rem 0 0;
}

.cp-close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem;
  border-radius: 6px;
  color: rgb(var(--slate-9));
  transition: all 0.12s ease;
  flex-shrink: 0;
}
.cp-close-btn:hover {
  color: rgb(var(--slate-12));
  background: rgb(var(--slate-3));
}

.cp-list {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 1.5rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.cp-condition-item {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.875rem 1rem;
  border: 1px solid rgb(var(--slate-4));
  border-radius: 10px;
  text-align: left;
  width: 100%;
  cursor: pointer;
  transition: all 0.15s ease;
  background: white;
}
.cp-condition-item:hover {
  border-color: rgb(var(--blue-7));
  background: rgb(var(--blue-1));
  box-shadow: 0 0 0 1px rgb(var(--blue-7));
}

.cp-cond-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  color: rgb(var(--slate-9));
}

.cp-cond-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.cp-cond-title {
  font-size: 14px;
  font-weight: 600;
  color: rgb(var(--slate-12));
  line-height: 1.3;
}

.cp-cond-desc {
  font-size: 12.5px;
  color: rgb(var(--slate-9));
  line-height: 1.4;
}
</style>
